/**
 * Compact-number formatting for the delivery-funnel UI (bd be0.9).
 *
 * Renders large integers as `200k`, `1.2M`, `770M`, `5.4B` so stage labels and
 * drop annotations stop clipping their band edges. The stat strip and currency
 * cells keep their exact formatting (precision matters there) — this helper is
 * scoped to the funnel-band labels and the per-domain table's large-count
 * columns.
 *
 * Locale-aware via `Intl.NumberFormat({notation: 'compact'})`. We lower-case
 * the "K" suffix specifically because the user's source quote was "200k
 * instead of 200.000" — `Intl` returns uppercase `K` by default on `en` locales.
 * `M` and `B` stay as `Intl` emits them.
 */

import {useMemo} from 'react';

/**
 * Format an integer in compact form.
 *
 * Examples (en locale):
 *   - 999            → "999"
 *   - 1_000          → "1k"
 *   - 200_000        → "200k"
 *   - 689_347        → "689.3k"
 *   - 1_200_000      → "1.2M"
 *   - 772_789_218    → "772.8M"
 *   - 5_400_000_000  → "5.4B"
 *
 * One decimal when it adds precision (`1.2M`); zero decimals when round
 * (`200k`). Negative numbers are supported but produce e.g. `-1.2M`.
 */
export function formatCompactNumber(value: number, locale?: string | string[]): string {
    const formatter = new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
    });
    return formatter.format(value).replace(/K\b/g, 'k');
}

/**
 * Format the full integer using locale-aware thousands grouping. Mirrors the
 * existing `formatInteger` helper — duplicated here so that components can
 * compose a compact label + a tooltip with the full number without crossing
 * module boundaries.
 *
 * Example (en): `772789218` → `"772,789,218"`.
 */
export function formatFullNumber(value: number, locale?: string | string[]): string {
    return new Intl.NumberFormat(locale, {maximumFractionDigits: 0}).format(value);
}

/**
 * React hook that returns a memoised `(value: number) => string` compact
 * formatter. Use this in components that render many numbers in the same
 * render pass — saves re-allocating `Intl.NumberFormat` for every cell.
 */
export function useCompactNumber(locale?: string | string[]): (value: number) => string {
    return useMemo(() => {
        const formatter = new Intl.NumberFormat(locale, {
            notation: 'compact',
            maximumFractionDigits: 1,
        });
        return (value: number) => formatter.format(value).replace(/K\b/g, 'k');
    }, [locale]);
}
